import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SceneDetails, { type SceneRef } from '../../../Breakdown/SceneDetails/SceneDetails';
import ElementSearch from '../../../../components/ElementSearch/ElementSearch';
import { ElementType, ElementWithUserNames } from '../../../../types/element.types';
import { SceneInfo } from '../../../../types/scene.types';
import { useElementUpdates } from '../../../../hooks/dataUpdates';
import './EditScene.css';

export type EditSceneDrawerPosition = 'default' | 'rightEdge';

/** 拍摄计划 / 元素管理等右侧打开场次编辑时的 `drawerTop`（固定值，躲开 TabBar 与各页顶栏） */
export const EDIT_SCENE_PANEL_DRAWER_TOP_PX = 76;

/** 不传 `drawerTop` 时默认顶距（与主 TabBar 高度一致） */
const DEFAULT_DRAWER_TOP = 36;

interface EditSceneProps {
  visible: boolean;
  onClose: () => void;
  sceneId: string;
  breakdownID: string;
  /** 当前资源是否允许编辑场次详情 */
  canEdit?: boolean;
  /** 抽屉位置：default 与 rightEdge 均贴视口右侧；rightEdge 用于元素管理等需套同一容器的调用方 */
  drawerPosition?: EditSceneDrawerPosition;
  /** 抽屉 top（px），不传则为 DEFAULT_DRAWER_TOP；面板内打开宜用 {@link EDIT_SCENE_PANEL_DRAWER_TOP_PX} */
  drawerTop?: number;
}

const EditScene: React.FC<EditSceneProps> = ({
  visible,
  onClose,
  sceneId,
  breakdownID,
  canEdit = false,
  drawerPosition = 'default',
  drawerTop = DEFAULT_DRAWER_TOP,
}) => {
  const { t } = useTranslation();
  // ElementSearch 状态管理
  const [elementSearchVisible, setElementSearchVisible] = useState(false);
  const [elementSearchType, setElementSearchType] = useState<ElementType>(ElementType.CAST);
  const [elements, setElements] = useState<ElementWithUserNames[]>([]);
  const drawerRef = useRef<HTMLDivElement>(null);
  const elementSearchContainerRef = useRef<HTMLDivElement>(null);
  const sceneDetailsRef = useRef<SceneRef>(null);
  // const { getBreakdownCache, getElementCache, getElementRelationCache } = useDataCache();
  const { createElementRelationData } = useElementUpdates();

  // 处理打开 ElementSearch
  const handleOpenElementSearch = useCallback((type: ElementType) => {
    setElementSearchType(type);
    setElementSearchVisible(true);
  }, []);

  // 处理关闭 ElementSearch：关闭时清除 tabs 的已选聚焦样式
  const handleCloseElementSearch = useCallback(() => {
    setElementSearchVisible(false);
    sceneDetailsRef.current?.clearActiveTab();
  }, []);

  // 处理 ElementSearch 选择元素
  const handleElementSearchChange = useCallback(async (element: any, isCreated?: boolean) => {
    if (!sceneId || !breakdownID) return;

    // 如果是新创建的场次元素，后端在创建时已经自动创建了关系，不需要再次创建
    if (isCreated) {
      return;
    }

    try {
      // 创建场次元素关系（仅当选择已存在的场次元素时）
      // 使用统一的更新 hook，自动更新缓存和通知订阅者
      await createElementRelationData(
        breakdownID,
        element.elementID,
        sceneId,
        {
          elementQtyInScene: element.elementMaxQtyInBreakdown || 1,
        }
      );
    } catch (error: any) {
      console.error('创建场次元素关系失败:', error);
      alert(t('scene.createElementRelationFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
    // 不再自动关闭搜索框，允许用户继续选择多个元素
    // 用户可以通过点击外部区域或关闭按钮来手动关闭
  }, [sceneId, breakdownID, createElementRelationData, t]);

  // 提取已选择的场次元素ID列表（按当前 tab 类型过滤，ElementSearch 只显示该类型的已选状态）
  const selectedElementIds = elements
    .filter(item => item.elementType === elementSearchType)
    .map(item => item.elementID);

  // 处理场次更新回调
  // 注意：场景数据的更新应该通过 useSceneUpdates hook 来处理
  // 这里只保留回调接口，实际更新由统一的 hooks 处理
  const handleSceneUpdate = useCallback((updatedSceneId: string, updatedData: Partial<SceneInfo>) => {
    // 场景数据更新由统一的 hooks 自动处理缓存和通知
    // 这里不需要手动操作缓存
  }, []);

  // 阻止点击抽屉内容时关闭
  const handleDrawerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // 点击编辑页外部关闭编辑页（仅判断本实例的抽屉与 ElementSearch，避免页上其他 .element-search 干扰）
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (drawerRef.current?.contains(target)) return;
      if (elementSearchContainerRef.current?.contains(target)) return;
      // 场次详情内外/日夜下拉通过 Portal 挂在 body 上，不在抽屉 DOM 内
      if (target instanceof Element && target.closest('.scene-detail-field-dropdown')) return;
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  // 仅当 ElementSearch 未打开时监听 window blur，避免在搜索里点击添加按钮等触发失焦导致抽屉被关闭
  useEffect(() => {
    if (!visible || elementSearchVisible) return;

    const handleWindowBlur = () => {
      onClose();
    };

    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [visible, elementSearchVisible, onClose]);

  if (!visible) return null;

  const isRightEdge = drawerPosition === 'rightEdge';

  return (
    <div
      className={`edit-scene-container ${isRightEdge ? 'edit-scene-container-right-edge' : ''}`}
      style={{ ['--edit-scene-drawer-top' as string]: `${drawerTop}px` }}
    >
      {/* 半透明遮罩层，点击关闭 */}
      <div
        className="edit-scene-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* 抽屉内容 */}
      <div className={`edit-scene-drawer ${isRightEdge ? 'edit-scene-drawer-right-edge' : ''}`} ref={drawerRef} onClick={handleDrawerClick}>
        <div className="edit-scene-content">
          <SceneDetails
            ref={sceneDetailsRef}
            sceneId={sceneId}
            visible={visible}
            onClose={onClose}
            onSceneUpdate={handleSceneUpdate}
            externalElementSearch={true}
            onOpenElementSearch={handleOpenElementSearch}
            onElementsChange={setElements}
            hideSummaryInfo={true}
            hideHeaderActions={true}
            showCloseButton={true}
            hideCategoryHeaderCopy={true}
            canEdit={canEdit}
          />
        </div>
      </div>

      {/* ElementSearch 弹窗 - 用 ref 包裹以便点击外部时只认本实例，不与页上其他 ElementSearch 混淆 */}
      <div ref={elementSearchContainerRef}>
        <ElementSearch
          value={null}
          onChange={handleElementSearchChange}
          visible={elementSearchVisible}
          onClose={handleCloseElementSearch}
          breakdownID={breakdownID}
          currentElementType={elementSearchType}
          sceneID={sceneId}
          selectedElementIds={selectedElementIds}
        />
      </div>
    </div>
  );
};

export default EditScene;
